// FRONT-1698: the composer's model control (flag `eddy.model-picker-composer`)
// — a model button plus the thinking slider, replacing the Fast/Thoughtful
// pills. `sessionId` is the conversation the composer belongs to; without one
// (the pre-session launcher composer) the control reads/writes only the
// person's standing pick, which seeds the next new chat.

import { useId, useState } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { ThinkingSlider } from '~/components/thinking-slider';
import {
  useChatModel,
  useModelPickerPrefs,
  usePickChatModel,
  useSetThinkingLevel,
  useStandingChatModel,
  useThinkingLevel,
  useUpdateModelPickerPrefs,
} from '~/hooks/queries/use-model-picker';
import {
  activeModelReasons,
  activeModelTopTiers,
  modelButtonLabel,
  modelControlReason,
  resolveActiveModel,
  shortModelName,
  thinkingControlReason,
  toggleStar,
} from './model-picker-logic';
import { ModelPickerDialog } from './model-picker-dialog';

const PILL =
  'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors active:scale-95 disabled:opacity-50';
const PILL_IDLE = 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50';

/** Opens the window only once prefs exist; before that the click is dropped, not queued. */
export function openWindowIfReady(prefs: unknown, open: () => void): boolean {
  if (!prefs) return false;
  open();
  return true;
}

export function ComposerModelPicker({
  sessionId,
  disabled,
}: {
  sessionId?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const prefsQuery = useModelPickerPrefs();
  const sessionQuery = useChatModel(sessionId);
  const standingQuery = useStandingChatModel(!sessionId);
  const pick = usePickChatModel(sessionId);
  const updatePrefs = useUpdateModelPickerPrefs();
  const thinkingQuery = useThinkingLevel(sessionId);
  const setThinking = useSetThinkingLevel(sessionId);

  const prefs = prefsQuery.data;
  const active = resolveActiveModel({
    sessionId,
    sessionState: sessionQuery.data,
    standingModel: standingQuery.data?.model,
    prefs,
  });
  const shortName = active.name ? shortModelName(active.name) : null;
  // The window only exists once prefs have loaded; before that a click is
  // ignored, never remembered — otherwise it would pop open by surprise later.
  const reason = modelControlReason({
    busy: !!disabled,
    prefsLoaded: !!prefs,
    prefsFailed: prefsQuery.isError,
  });
  const reasonId = useId();
  const openWindow = () => openWindowIfReady(prefs, () => setOpen(true));
  const thinkingReason = thinkingControlReason({
    busy: !!disabled,
    loaded: !!thinkingQuery.data,
    failed: thinkingQuery.isError,
    modelReasons: activeModelReasons(prefs, active.id),
  });

  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="ghost"
        size="xs"
        tooltip={reason ?? active.name ?? 'Choose a model'}
        aria-describedby={reason ? reasonId : undefined}
        data-testid="composer-model-button"
        aria-label={modelButtonLabel(shortName)}
        data-model-id={active.id ?? undefined}
        onClick={openWindow}
        disabled={reason !== null}
        // Fixed width, so the slider beside it never moves when the name changes.
        className={cn(PILL, PILL_IDLE, 'w-[9.5rem] justify-between')}
      >
        <span className="truncate">{shortName ?? 'Model'}</span>
        <CaretDownIcon size={11} weight="bold" className="shrink-0" />
      </Button>
      <ThinkingSlider
        value={thinkingQuery.data?.level}
        onChange={(level) => setThinking.mutate(level)}
        disabledReason={thinkingReason}
        cappedAtHigh={!activeModelTopTiers(prefs, active.id)}
        ariaLabel="Thinking level"
        testId="composer-thinking-slider"
      />
      {reason && (
        <span id={reasonId} className="sr-only" data-testid="composer-model-reason">
          {reason}
        </span>
      )}
      {prefs && (
        <ModelPickerDialog
          open={open}
          onOpenChange={setOpen}
          prefs={prefs}
          pickId={active.pickId}
          onPick={(id) => pick.mutate(id)}
          onReorder={(order) => updatePrefs.mutate({ order })}
          onToggleStar={(id) => updatePrefs.mutate({ starred: toggleStar(prefs.starred ?? [], id) })}
        />
      )}
    </div>
  );
}
